import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import RelatedPostList from './RelatedPost';
import BlogShare from './BlogShare';
import TagItems from './TagItems';
import PrevNextPost from './PrevNextPost';
import AuthorBio from './AuthorBio';
import BlogSidebar from './BlogSidebar';
import Loader from './Loader';
import PostContent from './PostContent';
import { GetPostBySlug, GetPostPreview } from '../services/apiservice';

/**
 * Shared layout for the blog detail and blog preview pages.
 *
 * preview={false} (default) — live published post, fetched by slug, includes SEO meta tags
 * preview={true}            — draft preview fetched by pk, shows a warning banner, no meta tags
 */
function BlogDetailView({ preview = false }) {
  const [blogPost, setBlogPost] = useState(null);
  const [blogURL, setBlogURL] = useState('');
  const [loading, setLoading] = useState(true);
  const { paramlink, pk } = useParams();

  useEffect(() => {
    const fetchPost = async () => {
      try {
        if (preview) {
          const data = await GetPostPreview(parseInt(pk, 10));
          setBlogPost(JSON.parse(data));
        } else {
          setBlogURL(window.location.href);
          const data = await GetPostBySlug(paramlink);
          setBlogPost(JSON.parse(data));
        }
      } catch (error) {
        console.error('Error fetching post:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [pk, paramlink, preview]);

  return (
    <div>
      {/* SEO meta tags — live posts only, not preview drafts */}
      {!preview && (
        <Helmet>
          {blogPost && (
            <>
              <meta property="og:image" content={blogPost.mediapath} />
              <meta name="twitter:image" content={blogPost.mediapath} />
              <meta name="author" content={blogPost.author || 'Data Beautifier'} />
              <meta name="publish_date" content={blogPost.posteddate} />
              <meta name="article:published_time" content={blogPost.posteddate} />
            </>
          )}
        </Helmet>
      )}

      <Header />

      {loading ? (
        <Loader />
      ) : blogPost ? (
        <section className="blog-area blog-section blog-details-area ptb-20">
          <div className="container-block-fluid">
            <div className="row">
              <div className="full-col col-lg-8 col-12">

                {/* Preview-mode warning banner */}
                {preview && (
                  <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', padding: '10px 16px', marginBottom: '16px', fontWeight: 600 }}>
                    Preview Mode — this post is not published yet
                  </div>
                )}

                <div className="blog-details-left-area">
                  <div className="blog-details-single-post" data-aos="fade-up" data-aos-duration="1400">
                    <div className="blog-details-img">
                      <img src={blogPost.mediapath} alt={blogPost.title} />
                    </div>
                    <ul>
                      <li><i className="bx bx-time"></i> {blogPost.posteddate}</li>
                      <li>
                        <a href="#">
                          <i className="bx bx-street-view"></i>
                          {blogPost.views}
                        </a>
                      </li>
                      <li>
                        <a href="#">
                          <i className="bx bx-comment"></i>
                          {blogPost.readtime} mins read
                        </a>
                      </li>
                    </ul>
                    <div className="tag-items pb-20">
                      <TagItems postSlug={paramlink} />
                    </div>
                    <h3>{blogPost.title}</h3>
                    <PostContent html={blogPost.description} className='dropcap-paragraph' />
                  </div>

                  <div className="blog-details-sheare" data-aos="fade-up" data-aos-duration="2000">
                    <BlogShare blogURL={blogURL} />
                  </div>

                  <div className="prev-next" data-aos="fade-up" data-aos-duration="2200">
                    <PrevNextPost postSlug={paramlink} />
                  </div>

                  <div className="profile" data-aos="fade-up" data-aos-duration="2400">
                    <AuthorBio postData={blogPost} />
                  </div>

                  <RelatedPostList postSlug={paramlink} />
                </div>
              </div>
              <div className="full-col col-lg-4 col-12">
                <BlogSidebar />
              </div>
            </div>
          </div>
        </section>
      ) : (
        <div className="container ptb-20">
          <p>{preview ? 'Preview not available. The post may not exist.' : 'Blog post not found.'}</p>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default BlogDetailView;
